import { frequencyMap, periodMap } from '~/src/dash/apiV2/reporting/reports';
import { reportTypeIDToTKey } from '~/src/dash/apiV2/reporting/reportTypes';
import { IFilterRowObject } from '~/src/dash/components/filters/FilterRowV2';
import {
  IListViewTableColumns,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import {
  DateTimeRender,
  EllipsisWithTooltipRender,
  SimpleTranslationRender,
} from '~/src/dash/components/tables/ListViewTable/ListViewTableRow/templates';
import { ct } from '~/src/dash/publicaUI';

export interface IReportingListConfig {
  cypressClassName: string;
  defaultState: {
    order: Order;
    orderBy: string;
    page: number;
    rowsPerPage?: RowsPerPage;
  };
  activeColumnKeys: string[]; // This can be used to track which columns are active
  filters: Record<string, IFilterRowObject>;
  columns: IListViewTableColumns;
}

export const reportingListConfig: IReportingListConfig = {
  cypressClassName: 'reportinglistview',
  activeColumnKeys: ['Type', 'UpdatedAt', 'Recipients', 'Frequency', 'Period'],
  defaultState: {
    order: 'asc',
    orderBy: 'ID',
    page: 0,
    rowsPerPage: 10,
  },
  // due to the custom logic in ReportingListView, you will have to update the populateFiltersAndData, handleApplySelect, handleClearFilters functions if you edit the filters section
  filters: {
    User: {
      disableSort: true,
      key: 'User',
      tKey: 'User',
      selectedLimit: 1,
      // selectedOptionIDs: new Set([0]), Note the code in ReportingListView will default this to the current user id. therefore we don't need to set it here
      selectedOptionIDsOnReset: new Set(['-1']),
      isSelectionRequired: true,
      isCheckboxHidden: true,
      displayLabelWithSelection: true,
    },
    Schedule: {
      disableSort: true,
      key: 'Schedule',
      tKey: 'Status',
      selectedLimit: 1,
      isCheckboxHidden: true,
      displayLabelWithSelection: true,
      selectedOptionIDs: new Set([0]),
      isSelectionRequired: true,
      selectedOptionIDsOnReset: new Set([0]),
    },
    ReportType: {
      key: 'ReportType',
      tKey: 'ReportType',
      selectedLimit: 0,
      disableSort: true,
    },
  },
  columns: {
    ID: {
      isHidden: true,
      isSearchable: true,
      key: 'ID',
      label: 'ID',
      requestKey: 'ID',
      responseKey: 'ID',
      valueType: 'number',
    },
    Status: {
      defaultOrderDirection: 'desc',
      headerRenderType: 'switch',
      key: 'Status',
      tKey: 'Scheduled',
      label: 'Scheduled',
      requestKey: '',
      responseKey: 'Active',
      valueType: 'boolean',
      formatValue: (value: string) => {
        return value === 'true';
      },
    },
    Name: {
      cellJSX: EllipsisWithTooltipRender,
      cellRenderType: 'name_link',
      defaultOrderDirection: 'asc',
      headerRenderType: 'name',
      isSearchable: true,
      key: 'Name',
      label: 'Name',
      tKey: 'Name',
      requestKey: '',
      responseKey: 'Name',
      valueType: 'string',
    },
    Type: {
      cellJSX: SimpleTranslationRender,
      cellRenderType: 'string',
      defaultOrderDirection: 'asc',
      headerRenderType: 'string',
      key: 'Type',
      label: 'Type',
      tKey: 'Type',
      requestKey: '',
      responseKey: 'ReportType',
      valueType: 'string',
      formatValue: (value: string) => {
        return ct(reportTypeIDToTKey[value]);
      },
    },
    UpdatedAt: {
      cellJSX: DateTimeRender,
      cellRenderType: 'date',
      defaultOrderDirection: 'desc',
      headerRenderType: 'date',
      isDate: true,
      key: 'UpdatedAt',
      label: 'Last Updated',
      tKey: 'LastUpdated',
      orderByKey: 'updated_At',
      requestKey: '',
      responseKey: 'UpdatedAt',
      sortLabel: 'Last Updated',
      sortLabelTKey: 'LastUpdated',
      valueType: 'date',
    },
    Recipients: {
      cellRenderType: 'number',
      defaultOrderDirection: 'asc',
      headerRenderType: 'number',
      formatValue: (value: string) => {
        return value ? value.length : 0;
      },
      key: 'Recipients',
      label: 'Recipients',
      tKey: 'Recipients',
      requestKey: '',
      responseKey: 'Recipients',
      valueType: 'array',
    },
    Frequency: {
      cellJSX: SimpleTranslationRender,
      cellRenderType: 'string',
      defaultOrderDirection: 'asc',
      headerRenderType: 'string',
      key: 'Frequency',
      label: 'Frequency',
      tKey: 'Frequency',
      requestKey: '',
      responseKey: 'Frequency',
      valueType: 'string',
      formatValue: (value: string) => {
        return ct(frequencyMap[value]);
      },
    },
    Period: {
      cellJSX: SimpleTranslationRender,
      cellRenderType: 'string',
      defaultOrderDirection: 'asc',
      headerRenderType: 'string',
      key: 'Period',
      label: 'Period',
      tKey: 'Period',
      requestKey: '',
      responseKey: 'Period',
      valueType: 'string',
      formatValue: (value: string) => {
        return ct(periodMap[value]);
      },
    },
    Actions: {
      cellRenderType: 'actions',
      disableSort: true,
      headerRenderType: 'actions',
      key: 'Actions',
      label: 'Actions',
      tKey: 'Actions',
      requestKey: '',
      responseKey: '',
      valueType: 'component',
    },
  },
};
